// SlimFreeOnboardingStep.tsx — Minimal setup for free-tier users.
// Collects only what we need for safety (allergies) and serving-size scaling
// (household composition). Skips the full premium wizard.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { fetchTags } from './TagService';
import { trackEvent, AnalyticsEvents } from '../../services/analytics';

type Tag = { tag_id: number; tag_name: string; type: string };

export interface SlimFreeData {
  numAdults: number;
  numKids: number;
  allergyTagIds: number[];
}

interface Props {
  onBack: () => void;
  onNext: (data: SlimFreeData) => void;
  saving?: boolean;
}

const SlimFreeOnboardingStep: React.FC<Props> = ({ onBack, onNext, saving }) => {
  const [numAdults, setNumAdults] = useState<number>(1);
  const [numKids, setNumKids] = useState<number>(0);
  const [allergies, setAllergies] = useState<Tag[]>([]);
  const [allergyTagIds, setAllergyTagIds] = useState<number[]>([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackEvent(AnalyticsEvents.ONBOARDING_STEP_VIEWED, { step_name: 'SlimFreeOnboarding' });
    fetchTags('allergy')
      .then(setAllergies)
      .catch(() => {})
      .finally(() => setLoadingTags(false));
  }, []);

  const toggleAllergy = (id: number, checked: boolean) => {
    setAllergyTagIds(prev =>
      checked ? [...prev, id] : prev.filter(x => x !== id)
    );
  };

  const handleSubmit = () => {
    if (numAdults + numKids < 1) {
      setError('At least one person in the household is required.');
      return;
    }
    setError(null);
    trackEvent(AnalyticsEvents.ONBOARDING_STEP_COMPLETED, {
      step_name: 'SlimFreeOnboarding',
      household_size: numAdults + numKids,
      allergy_count: allergyTagIds.length,
    });
    onNext({ numAdults, numKids, allergyTagIds });
  };

  return (
    <Box maxWidth={600} mx="auto">
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Quick setup
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Just a couple of questions so we can scale recipes to your household and flag any allergies. You can refine your profile later.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Household size */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Who's in your household?
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <TextField
              type="number"
              label="Adults"
              value={numAdults}
              onChange={e => setNumAdults(Math.max(0, parseInt(e.target.value) || 0))}
              inputProps={{ min: 0, max: 20 }}
              sx={{ width: { xs: '100%', sm: 200 } }}
            />
            <TextField
              type="number"
              label="Kids"
              value={numKids}
              onChange={e => setNumKids(Math.max(0, parseInt(e.target.value) || 0))}
              inputProps={{ min: 0, max: 20 }}
              sx={{ width: { xs: '100%', sm: 200 } }}
            />
          </Stack>

          {/* Allergies */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Any allergies in your household?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Optional, but recommended for safety. We'll flag matching recipes.
          </Typography>

          {loadingTags ? (
            <CircularProgress size={24} />
          ) : (
            <FormGroup row sx={{ mb: 1 }}>
              {allergies.map(tag => (
                <FormControlLabel
                  key={tag.tag_id}
                  control={
                    <Checkbox
                      checked={allergyTagIds.includes(tag.tag_id)}
                      onChange={e => toggleAllergy(tag.tag_id, e.target.checked)}
                    />
                  }
                  label={tag.tag_name}
                />
              ))}
            </FormGroup>
          )}

          {allergyTagIds.length === 0 && !loadingTags && (
            <Chip label="No allergies" size="small" variant="outlined" sx={{ mt: 1 }} />
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={onBack} disabled={saving}>Back</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {saving ? 'Setting up…' : 'Finish'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SlimFreeOnboardingStep;
